/* CozeCats: auto-apply promo banner discount + pre-order variants.
   Settings & strings: window.CZPromo (snippets/cz-promo-config.liquid).
   Prices and discount amounts always come from Shopify; nothing is calculated here.
   Multi-code: Theme settings > Promo & Pre-order > Discount codes accepts a comma-separated list
   (e.g. "PRIME10,PRIME20"). Each banner applies the code in its own data-code, and codes already
   on the cart are kept, so several codes can live on one cart. */
(() => {
  const cfg = window.CZPromo;
  if (!cfg || window.__czPromoLoaded) return;
  window.__czPromoLoaded = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  const sameCode = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  const cartCodes = (cart) => (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []);

  const getCart = () =>
    fetch(`${root}cart.js`, { credentials: "same-origin", headers: { Accept: "application/json" } }).then((res) => {
      if (!res.ok) throw new Error(`cart.js ${res.status}`);
      return res.json();
    });

  // Note: `discount` replaces every code on the cart, so callers always pass the full list.
  const setCartCodes = (codes) =>
    fetch(`${root}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ discount: codes.join(",") }),
    }).then((res) => {
      if (!res.ok) throw new Error(`cart/update.js ${res.status}`);
      return res.json();
    });

  const refreshCart = () => {
    const drawer = document.querySelector("m-cart-drawer");
    if (drawer && typeof drawer.onCartDrawerUpdate === "function") drawer.onCartDrawerUpdate();
    const cartPage = document.querySelector("m-cart");
    if (cartPage && typeof cartPage.onCartUpdate === "function") cartPage.onCartUpdate();
  };

  /* ---------- Toast: theme notification + polite live region ---------- */
  let liveRegion;
  const toast = (message, type) => {
    const notification = window.MinimogTheme && window.MinimogTheme.Notification;
    if (notification) {
      notification.show({ target: document.body, method: "appendChild", type, message, last: 4000, sticky: true });
    }
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.className = "m:visually-hidden";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    setTimeout(() => (liveRegion.textContent = message), 50);
  };

  const legacyCopy = (text) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (e) {}
    document.body.removeChild(ta);
    return ok;
  };

  const copyText = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
    }
    return Promise.resolve(legacyCopy(text));
  };

  /* =====================================================================
     1. AUTO-APPLY DISCOUNT (multi-code)
     ===================================================================== */
  const D = cfg.discount || {};

  // Allowed codes: D.codes (array) if the config provides it, otherwise D.code split on "," ";" or new line.
  const toCodeList = (value) =>
    (Array.isArray(value) ? value : String(value || "").split(/[,;\n]/))
      .map((c) => String(c || "").trim())
      .filter(Boolean);
  const allowedCodes = toCodeList(D.codes || D.code);
  const isAllowed = (code) => allowedCodes.some((c) => sameCode(c, code));
  const codeKey = (code) => String(code || "").trim().toLowerCase();

  // Toast / label strings from cz-promo-config.liquid may embed the whole setting ("PRIME10,PRIME20").
  // Replace that list with the single code the shopper clicked.
  const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const codeAlt = allowedCodes.map(escapeRe).join("|");
  const codeListRe = allowedCodes.length > 1 ? new RegExp(`(?:${codeAlt})(?:\\s*[,;]\\s*(?:${codeAlt}))+`, "gi") : null;
  const forCode = (text, code) =>
    typeof text === "string" && codeListRe ? text.replace(codeListRe, String(code || "").trim().toUpperCase()) : text;

  if (D.enabled && allowedCodes.length) {
    let busy = false;
    const lastToasts = {}; // last toast per code (lowercase key)

    // All banner buttons with an allowed code; pass `code` to get only that code's buttons.
    const bannerButtons = (code) =>
      Array.from(document.querySelectorAll(".cd-code[data-code]")).filter(
        (btn) => isAllowed(btn.dataset.code) && (code === undefined || sameCode(btn.dataset.code, code))
      );

    const setBannerApplied = (code, applied) => {
      bannerButtons(code).forEach((btn) => {
        if (btn.dataset.czLabel === undefined) btn.dataset.czLabel = btn.getAttribute("aria-label") || "";
        btn.classList.toggle("is-copied", applied);
        btn.classList.toggle("cz-is-applied", applied);
        btn.dataset.czApplied = applied ? "true" : "";
        btn.setAttribute("aria-label", applied ? forCode(D.appliedLabel, btn.dataset.code) : btn.dataset.czLabel);

        let tag = btn.querySelector(".cz-code-applied");
        if (applied && !tag) {
          tag = document.createElement("span");
          tag.className = "cz-code-applied";
          tag.textContent = forCode(D.applied, btn.dataset.code);
          btn.appendChild(tag);
        } else if (!applied && tag) {
          tag.remove();
        }
      });
    };

    const isApplied = (cart, code) => {
      const entry = cartCodes(cart).find((d) => sameCode(d.code, code));
      return !!entry && (cart.item_count === 0 || entry.applicable === true);
    };

    const applyFromBanner = async (btn) => {
      const code = btn.dataset.code.trim();
      copyText(code);

      if (btn.dataset.czApplied === "true") {
        const last = lastToasts[codeKey(code)];
        if (last) toast(last.message, last.type);
        return;
      }
      if (busy) return;
      busy = true;
      btn.setAttribute("aria-busy", "true");

      let message = forCode(D.toastCopied, code);
      let type = "warning";
      let applied = false;
      try {
        const before = await getCart();
        // Keep every code already on the cart (e.g. PRIME10), then add this one (e.g. PRIME20).
        const otherCodes = cartCodes(before)
          .map((d) => d.code)
          .filter((c) => !sameCode(c, code));

        // Sets the discount cookie so checkout still gets the code (e.g. cart created later).
        await fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`, {
          credentials: "same-origin",
        }).catch(() => {});

        const cart = await setCartCodes(otherCodes.concat(code));
        const entry = cartCodes(cart).find((d) => sameCode(d.code, code));
        if (entry && cart.item_count === 0) {
          message = forCode(D.toastSaved, code);
          type = "success";
          applied = true;
        } else if (entry && entry.applicable) {
          message = forCode(D.toastApplied, code);
          type = "success";
          applied = true;
        }
        refreshCart();
      } catch (error) {
        console.warn("[CZPromo] Could not apply discount:", error);
      }

      busy = false;
      btn.removeAttribute("aria-busy");
      setBannerApplied(code, applied);
      lastToasts[codeKey(code)] = { message, type };
      toast(message, type);
    };

    // Capture phase: runs before the banner's own copy-only handler and replaces it.
    window.addEventListener(
      "click",
      (event) => {
        const btn = event.target.closest && event.target.closest(".cd-code[data-code]");
        if (!btn || !isAllowed(btn.dataset.code)) return;
        event.preventDefault();
        event.stopPropagation();
        applyFromBanner(btn);
      },
      true
    );

    // Remove (×) in cart drawer / cart page.
    document.addEventListener("click", async (event) => {
      const btn = event.target.closest && event.target.closest("[data-cz-discount-remove]");
      if (!btn) return;
      event.preventDefault();
      if (btn.disabled) return;
      btn.disabled = true;
      const code = btn.dataset.code;
      try {
        const cart = await getCart();
        const remaining = cartCodes(cart)
          .map((d) => d.code)
          .filter((c) => !sameCode(c, code));
        await setCartCodes(remaining);
        if (isAllowed(code)) {
          setBannerApplied(code, false);
          delete lastToasts[codeKey(code)];
        }
      } catch (error) {
        console.warn("[CZPromo] Could not remove discount:", error);
        btn.disabled = false;
      }
      refreshCart();
    });

    // Codes saved on the cart that do not apply yet ("Applies at checkout").
    // Only needed when Liquid does not expose cart.discount_codes (data-cz-codes-liquid is empty).
    const syncPendingRows = (cart) => {
      document.querySelectorAll("[data-cz-discount-list]").forEach((list) => {
        if (list.dataset.czCodesLiquid === "true") return;
        const template = list.parentElement && list.parentElement.querySelector("template[data-cz-discount-pending-template]");
        if (!template) return;
        cartCodes(cart)
          .filter((d) => !d.applicable)
          .forEach((d) => {
            const key = String(d.code).toLowerCase();
            const exists = Array.from(list.querySelectorAll("[data-cz-code]")).some((li) => li.dataset.czCode === key);
            if (exists) return;
            const row = template.content.firstElementChild.cloneNode(true);
            row.dataset.czCode = key;
            row.querySelector("[data-cz-code-text]").textContent = String(d.code).toUpperCase();
            row.querySelector("[data-cz-discount-remove]").dataset.code = d.code;
            list.appendChild(row);
          });
        list.classList.toggle("m:hidden", list.children.length === 0);
      });
    };

    const needsPendingSync = () =>
      Array.from(document.querySelectorAll("[data-cz-discount-list]")).some((list) => list.dataset.czCodesLiquid !== "true");

    let syncTimer;
    const scheduleSync = () => {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(() => {
        if (!needsPendingSync()) return;
        getCart().then(syncPendingRows).catch(() => {});
      }, 150);
    };

    const init = () => {
      // Theme re-renders these containers on every cart change.
      document.querySelectorAll("[data-minimog-cart-discounts]").forEach((container) => {
        new MutationObserver(scheduleSync).observe(container, { childList: true });
      });

      const buttons = bannerButtons();
      const needBanner = buttons.length > 0;
      if (!needBanner && !needsPendingSync()) return;
      getCart()
        .then((cart) => {
          if (needBanner) {
            // Mark each banner code that is already on the cart (one check per distinct code).
            const seen = {};
            buttons.forEach((btn) => {
              const key = codeKey(btn.dataset.code);
              if (seen[key]) return;
              seen[key] = true;
              if (isApplied(cart, btn.dataset.code)) setBannerApplied(btn.dataset.code, true);
            });
          }
          syncPendingRows(cart);
        })
        .catch(() => {});
    };

    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
    else init();
  }

  /* =====================================================================
     2. PRE-ORDER
     ===================================================================== */
  const P = cfg.preorder || {};
  if (!P.enabled) return;

  const preorderMap = {};
  const loadPreorderData = (scope) => {
    scope.querySelectorAll("script[data-cz-preorder-data]").forEach((el) => {
      if (el.dataset.czLoaded) return;
      el.dataset.czLoaded = "true";
      try {
        Object.assign(preorderMap, JSON.parse(el.textContent));
      } catch (error) {
        console.warn("[CZPromo] Invalid pre-order data", error);
      }
    });
  };
  const getPreorder = (variantId) => (variantId ? preorderMap[String(variantId)] || null : null);
  const shipsByText = (info) => (info.shipDate ? P.shipsBy.replace("__DATE__", info.shipDate) : P.fallbackText);
  const propertyText = (info) => (info.shipDate ? P.shipsBeforeValue.replace("__DATE__", info.shipDate) : P.fallbackText);

  const updateForm = (form, variantId) => {
    if (!form) return;
    const info = getPreorder(variantId);

    // Toggle the `name`, not just `disabled`: Shopify's dynamic checkout (Shop Pay / More payment options)
    // collects every [name^="properties"] field and ignores `disabled`, so a nameless input is the only safe "off".
    form.querySelectorAll("[data-cz-preorder-prop]").forEach((input) => {
      input.disabled = !info;
      if (info) {
        if (input.dataset.czName) input.setAttribute("name", input.dataset.czName);
        if (input.dataset.czPreorderProp === "label") input.value = propertyText(info);
      } else {
        if (!input.dataset.czName && input.getAttribute("name")) input.dataset.czName = input.getAttribute("name");
        input.removeAttribute("name");
      }
    });

    // Text only: the theme already handled disabled/sold-out state before this runs.
    const buttonText = form.querySelector('[name="add"] .m-add-to-cart--text');
    if (buttonText) {
      if (info) buttonText.textContent = P.buttonText;
      else if (buttonText.textContent.trim() === P.buttonText) buttonText.textContent = window.MinimogStrings.addToCart;
    }

    // Dynamic checkout skips the cart, so the free-mat gift would be lost.
    const dynamicCheckout = form.querySelector(".m-product-dynamic-checkout");
    if (dynamicCheckout) dynamicCheckout.classList.toggle("cz-hidden", !!info);

    const note = form.querySelector("[data-cz-preorder-note]");
    if (note) {
      note.hidden = !info;
      if (info) note.textContent = `${shipsByText(info)} · ${P.freeMatIncluded}`;
    }
    form.classList.toggle("cz-is-preorder", !!info);
  };

  // Badge on value V of option P (options cascade top -> bottom, e.g. Style > Color > Riser):
  // take every variant with options 1..P-1 = current selection and option P = V.
  // Show the badge when none of them is in stock and at least one is pre-order.
  // The first option never gets a badge when the product has more than one option.
  const branchIsPreorder = (variants, current, position, value) => {
    const branch = variants.filter(
      (v) =>
        Array.isArray(v.options) &&
        v.options[position - 1] === value &&
        v.options.slice(0, position - 1).every((selected, i) => selected === current.options[i])
    );
    let hasPreorder = false;
    for (const v of branch) {
      const preorder = !!getPreorder(v.id);
      if (v.available && !preorder) return false; // in stock somewhere below
      if (preorder) hasPreorder = true;
    }
    return hasPreorder;
  };

  const updateBadges = (picker) => {
    const variants = (picker.productData && picker.productData.variants) || [];
    const current = picker.currentVariant;
    const optionCount = current && Array.isArray(current.options) ? current.options.length : 0;

    picker.querySelectorAll(".m-product-option--node").forEach((node) => {
      const position = Number(node.dataset.optionPosition);
      const skip = !current || !position || (position === 1 && optionCount > 1);
      const info = !skip && branchIsPreorder(variants, current, position, node.dataset.value);

      if (node.tagName === "OPTION") {
        if (node.dataset.czText === undefined) node.dataset.czText = node.textContent.trim();
        const text = info ? `${node.dataset.czText} – ${P.badgeText}` : node.dataset.czText;
        if (node.textContent !== text) node.textContent = text;
        return;
      }

      const label = node.querySelector(".m-product-option--node__label");
      if (!label) return;
      let badge = label.querySelector(".cz-preorder-badge");
      if (info && !badge) {
        badge = document.createElement("span");
        badge.className = "cz-preorder-badge";
        badge.textContent = P.badgeText;
        label.appendChild(badge);
      } else if (!info && badge) {
        badge.remove();
      }
      if (label.classList.contains("cz-has-preorder") !== !!info) label.classList.toggle("cz-has-preorder", !!info);
    });
  };

  const updatePicker = (picker) => {
    if (!picker.productData) return;
    const variant = picker.currentVariant;
    const variantId = variant ? variant.id : null;
    const info = getPreorder(variantId);
    const productId = picker.dataset.productId;
    const wrapper = picker.productWrapper || picker.closest(".m-main-product--wrapper") || document;

    wrapper.querySelectorAll(`product-form form[data-product-id="${productId}"]`).forEach((form) => updateForm(form, variantId));
    document
      .querySelectorAll(`sticky-atc[data-product-id="${productId}"] form`)
      .forEach((form) => updateForm(form, variantId));

    wrapper.querySelectorAll("[data-cz-preorder-trust]").forEach((row) => {
      row.hidden = !info;
      if (!info) return;
      const date = row.querySelector("[data-cz-ship-date]");
      if (date) date.textContent = info.shipDate || P.fallbackText; // line 1 stays as written in the snippet
    });
    wrapper.querySelectorAll("[data-cz-delivery-default]").forEach((row) => (row.hidden = !!info));
    wrapper.querySelectorAll("[data-vm-stock-status]").forEach((status) => {
      status.classList.toggle("is-hidden", !!info || !(variant && variant.available));
    });

    updateBadges(picker);
  };

  const initPicker = (picker, attempt = 0) => {
    if (picker.dataset.czPreorderInit) return;
    if (!picker.productData) {
      // variant-picker initialises late in the theme editor.
      if (attempt < 20) setTimeout(() => initPicker(picker, attempt + 1), 150);
      return;
    }
    picker.dataset.czPreorderInit = "true";
    updatePicker(picker);
  };

  const initScope = (scope) => {
    loadPreorderData(scope);
    if (Object.keys(preorderMap).length === 0) return;
    const pickers = scope.matches && scope.matches("variant-picker") ? [scope] : scope.querySelectorAll("variant-picker");
    if (!pickers.length) return;
    customElements.whenDefined("variant-picker").then(() => pickers.forEach((picker) => initPicker(picker)));
  };

  // Runs after the theme's own change handlers (variant-picker / sticky-atc listen on the element itself).
  document.addEventListener("change", (event) => {
    if (Object.keys(preorderMap).length === 0) return;
    const picker = event.target.closest && event.target.closest("variant-picker");
    if (picker) {
      updatePicker(picker);
      return;
    }
    const sticky = event.target.closest && event.target.closest("sticky-atc");
    if (sticky) {
      const form = sticky.querySelector("form");
      const select = form && form.querySelector('[name="id"]');
      updateForm(form, select && select.value);
    }
  });

  const start = () => {
    initScope(document);
    // Quick view and other sections inject product forms later.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) =>
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches("variant-picker, script[data-cz-preorder-data]") || node.querySelector("script[data-cz-preorder-data]")) {
            initScope(node.matches("script") ? node.parentElement || document : node);
          }
        })
      );
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("shopify:section:load", (event) => initScope(event.target));
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
})();